import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {
  FootballGameLog,
  FootballGameLogTypes,
  Game,
  GameStatuses,
  GameUser,
  generateArray,
  VolleyballGameLog
} from '@mtgame/core';

@Component({
  selector: 'mtg-volleyball-game-timeline',
  templateUrl: './volleyball-game-timeline.component.html',
  styleUrls: ['./volleyball-game-timeline.component.scss', '../../game/styles/timeline.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VolleyballGameTimelineComponent implements OnChanges {
  @Input()
  game: Game;
  @Input()
  gameLogs: VolleyballGameLog[];
  @Input()
  gameUsers: GameUser[];
  @Input()
  sort: 'asc' | 'desc' = 'desc';
  @Input()
  reverse: boolean;
  @Output()
  update = new EventEmitter();
  @Output()
  delete = new EventEmitter();

  gameStatuses = GameStatuses;
  selectedPeriod: number;
  scoresByRally: {[key: number]: {[key: number]: [number, number]}};
  scoreBySet: {[key: number]: [number, number]};
  onlyHighlights = false;
  onlyScoreLogs = false;

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.gameUsers || changes.gameLogs || changes.game) && this.gameLogs && this.gameUsers && this.game) {
      const teamUserIds = this.gameUsers.filter(u => u.teamUser.teamId === this.game.team.id).map(u => u.id);
      const competitorTeamUserIds = this.gameUsers.filter(u => u.teamUser.teamId === this.game.competitorTeam.id).map(u => u.id);

      this.scoresByRally = Object.assign([], this.gameLogs)
        .reverse()
        .filter(item => item.isScoreType() || item.isFaultType())
        .reduce((acc, item) => {
          if (!acc[item.set]) {
            acc[item.set] = {
              0: [0, 0]
            };
          }
          if (!acc[item.set][item.rally]) {
            if (item.rally === 1) {
              acc[item.set][item.rally] = [0, 0];
            } else {
              const rallies = Object.keys(acc[item.set]);
              const previousRally = rallies[rallies.length - 1];
              acc[item.set][item.rally] = Object.assign([], acc[item.set][previousRally]);
            }
            const teamWon = item.isScoreType() && (teamUserIds.indexOf(item.gameUserId) > -1 || (!item.gameUserId && item.teamId === this.game.team.id)) ||
              item.isFaultType() && (competitorTeamUserIds.indexOf(item.gameUserId) > -1 || (!item.gameUserId && item.teamId === this.game.competitorTeam.id));
            if (teamWon) {
              acc[item.set][item.rally][0]++;
            } else {
              acc[item.set][item.rally][1]++;
            }
          }
          return acc;
        }, {});
      this.scoreBySet = Object.keys(this.game.scoreByPeriod || {}).reduce((acc, set) => {
        if (+set === 1) {
          acc[set] = [0, 0];
        } else {
          acc[set] = Object.assign([], acc[+set - 1]);
        }
        if (this.game.scoreByPeriod[set][0] > this.game.scoreByPeriod[set][1]) {
          acc[set][0]++;
        } else {
          acc[set][1]++;
        }
        return acc;
      }, {});
      if (this.sort === 'asc') {
        this.gameLogs = this.gameLogs.slice().reverse();
      }
    }
  }

  isSetHeadingVisible(currentLog: VolleyballGameLog, previousLog: VolleyballGameLog): boolean {
    if (!currentLog || !previousLog) {
      return true;
    }
    return currentLog.set !== previousLog.set;
  }

  isRallyScoreVisible(currentLog: VolleyballGameLog, previousLog: VolleyballGameLog): boolean {
    if (!currentLog) {
      return false;
    }
    return Boolean(
      (!previousLog || previousLog.rally !== currentLog.rally) && this.scoresByRally && this.scoresByRally[currentLog.set] &&
      this.scoresByRally[currentLog.set][currentLog.rally]
    );
  }

  get sets(): number[] {
    const setsCount = Object.keys(this.game.scoreByPeriod).length;
    if (setsCount === 0) {
      return [];
    }
    return generateArray(setsCount);
  }

  get lastLog(): VolleyballGameLog {
    return this.gameLogs ? this.gameLogs[this.gameLogs.length - 1] : null;
  }

  get firstLog(): VolleyballGameLog {
    return this.gameLogs ? this.gameLogs[0] : null;
  }

  toggleOnlyHighlights() {
    this.onlyHighlights = !this.onlyHighlights;
  }

  toggleOnlyScoreLogs() {
    this.onlyScoreLogs = !this.onlyScoreLogs;
  }

  identify(index, item) {
    return item.uniqueId;
  }
}
